import { useCallback, useMemo } from "react";

import { type CriteriaFilterValue } from "@stacklet/ui";
import { useFormContext, useWatch } from "react-hook-form";

import { type FilterElementInput } from "app/utils/filters";

import {
  buildProviderFilters as buildProviderFiltersUtil,
  combineFilters as combineFiltersUtil,
} from "./filterUtils";

interface UseProviderFilterCombinerProps {
  /** Field name to use for provider filtering */
  fieldName: string;
  /** Operator to use for provider filtering */
  operator: string;
}

/**
 * Hook that watches provider selection from form context and creates a memoised multiple filter
 * that includes provider filters
 */
export function useProviderFilterCombiner({
  fieldName,
  operator,
}: UseProviderFilterCombinerProps) {
  const { control } = useFormContext();
  const providerFilters = useWatch({ control, name: "providerFilters" }) as
    | CriteriaFilterValue[]
    | null
    | undefined;

  const providers = useMemo(() => {
    return (
      providerFilters?.flatMap((criteriaFilterValue) =>
        (criteriaFilterValue.value ?? []).flatMap((value) => {
          const provider = value?.id ?? value?.name;
          return provider ? [provider] : [];
        }),
      ) ?? []
    );
  }, [providerFilters]);

  const combineFilters = useCallback(
    (searchFilter: FilterElementInput | null): FilterElementInput | null => {
      const providerFilters = buildProviderFiltersUtil(
        providers,
        fieldName,
        operator,
      );
      return combineFiltersUtil(searchFilter, providerFilters);
    },
    [providers, fieldName, operator],
  );

  return { combineFilters, providers };
}
